import { Capacitor } from '@capacitor/core';

/**
 * A pergunta "estou rodando dentro do app nativo?", num lugar só.
 *
 * A mesma base de código serve a web e ao app, e um punhado de comportamentos precisa divergir: a
 * câmera (input de arquivo x plugin nativo), o banner de cookies (que não faz sentido dentro de um
 * app), e a navegação para fora (link comum x Browser/AppLauncher). Sem um ponto único, cada um
 * desses lugares acabaria com a sua própria forma de perguntar -- e a forma errada é fácil de
 * escrever: `navigator.userAgent`, `window.Capacitor` solto, checar o protocolo da URL.
 *
 * `Capacitor.isNativePlatform()` responde false na web, inclusive quando o bundle é o do
 * build:native aberto no navegador -- é a runtime nativa que responde, não o build. Isso é o que
 * permite testar as duas metades sem trocar de build.
 *
 * O @capacitor/core é seguro de importar na web: o pacote é JS puro e, sem a ponte nativa embaixo,
 * as chamadas simplesmente respondem "não sou nativo". Os PLUGINS (câmera, browser, app) é que
 * precisam de cuidado -- cada um deles fica atrás de um `ehNativo()` no ponto de uso.
 */

/** true só dentro do app empacotado (Android ou iOS). */
export function ehNativo() {
	return Capacitor.isNativePlatform();
}

/** 'android' | 'ios' | 'web'. Para o punhado de casos em que as duas plataformas divergem. */
export function plataforma() {
	return Capacitor.getPlatform();
}

/**
 * true no navegador de um aparelho Android — nunca dentro do app. Existe para a tarja que convida a
 * baixar o app (pages/Home/TarjaApp.jsx): o link da Play Store só serve no Android.
 *
 * Aqui o userAgent é a fonte certa, ao contrário do que o comentário do topo condena: lá a pergunta é
 * "estou no app?", que a runtime nativa responde; aqui é "em que sistema este NAVEGADOR roda?", e
 * para essa o `Capacitor.getPlatform()` só sabe dizer 'web'. Resolução de tela não serve: um iPhone
 * ou uma janela estreita no computador passariam por celular, e o link não leva a lugar nenhum neles.
 *
 * `userAgentData.platform` primeiro (Chromium, onde o UA tende a ser congelado), o UA depois
 * (Firefox, Samsung Internet antigo). Com "versão para computador" ligado, o Chrome se apresenta como
 * Linux e a resposta é false — quem pediu o site de computador não está procurando app.
 */
export function ehAndroidNoNavegador() {
	if (ehNativo() || typeof navigator === 'undefined') return false;
	const plataformaDeclarada = navigator.userAgentData?.platform;
	if (plataformaDeclarada) return plataformaDeclarada === 'Android';
	return /Android/i.test(navigator.userAgent || '');
}
